import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  listMyWatchlists, createWatchlist, updateWatchlist, deleteWatchlist,
} from "@/lib/watchlists.functions";
import { SHIFT_ROLES } from "@/lib/pay-rates";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

type Watchlist = {
  id: string;
  name: string;
  role_types: string[] | null;
  min_hourly_rate: number | null;
  max_distance_km: number | null;
  only_dbs_eligible: boolean;
  active: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
};

export const Route = createFileRoute("/_authenticated/bartender/watchlist")({
  head: () => ({ meta: [
    { title: "Shift watchlist — The Hospitality Reserve" },
    { name: "description", content: "Get notified only about shifts that match your preferences: role, pay, distance, DBS and quiet hours." },
  ] }),
  component: WatchlistPage,
});

const empty: Omit<Watchlist, "id"> = {
  name: "My watchlist",
  role_types: [],
  min_hourly_rate: null,
  max_distance_km: null,
  only_dbs_eligible: false,
  active: true,
  quiet_hours_start: null,
  quiet_hours_end: null,
};

function WatchlistPage() {
  const list = useServerFn(listMyWatchlists);
  const create = useServerFn(createWatchlist);
  const update = useServerFn(updateWatchlist);
  const remove = useServerFn(deleteWatchlist);
  const [items, setItems] = useState<Watchlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<typeof empty>(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await list();
      setItems((r.watchlists ?? []) as Watchlist[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load watchlists.");
    } finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);

  const toggleRole = (role: string) => {
    setDraft((d) => {
      const cur = new Set(d.role_types ?? []);
      if (cur.has(role)) cur.delete(role); else cur.add(role);
      return { ...d, role_types: Array.from(cur) };
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await create({ data: {
        name: draft.name,
        role_types: draft.role_types ?? [],
        min_hourly_rate: draft.min_hourly_rate ?? null,
        max_distance_km: draft.max_distance_km ?? null,
        only_dbs_eligible: draft.only_dbs_eligible,
        active: draft.active,
        quiet_hours_start: draft.quiet_hours_start,
        quiet_hours_end: draft.quiet_hours_end,
      } });
      toast.success("Watchlist created.");
      setDraft(empty);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not create watchlist.");
    } finally { setBusy(false); }
  };

  const toggleActive = async (w: Watchlist) => {
    try {
      await update({ data: { id: w.id, active: !w.active } });
      await load();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
  };

  const del = async (id: string) => {
    if (!confirm("Delete this watchlist?")) return;
    try { await remove({ data: { id } }); await load(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
  };

  return (
    <div className="min-h-dvh bg-background">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Alerts</p>
            <h1 className="mt-3 font-display text-3xl">Shift watchlist</h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              Set filters so you only get notified when a nearby shift matches what you're looking for.
              Leave a filter blank to skip it. With no active watchlists, you'll receive every nearby shift alert.
            </p>
          </div>
          <Link to="/bartender" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">
            Back to feed
          </Link>
        </div>
        <span className="gold-divider mt-4" />

        <section className="mt-8 border border-border p-6 bg-card">
          <h2 className="font-display text-xl">New watchlist</h2>
          <div className="mt-4 grid gap-4">
            <div>
              <Label>Name</Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                maxLength={60}
              />
            </div>
            <div>
              <Label>Roles (leave empty for any)</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SHIFT_ROLES.map((r) => {
                  const on = (draft.role_types ?? []).includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRole(r)}
                      className={`border px-3 py-1.5 text-xs tracking-wide-luxury capitalize ${on ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground"}`}
                    >
                      {r.replace(/_/g, " ")}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Min hourly rate (£)</Label>
                <Input
                  type="number" min={0} step="0.5"
                  value={draft.min_hourly_rate ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, min_hourly_rate: e.target.value === "" ? null : Number(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Max distance (km)</Label>
                <Input
                  type="number" min={0} step="1"
                  value={draft.max_distance_km ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, max_distance_km: e.target.value === "" ? null : Number(e.target.value) }))}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draft.only_dbs_eligible}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, only_dbs_eligible: !!v }))}
              />
              Hide DBS-required shifts unless my DBS is on file
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Quiet hours start (UTC)</Label>
                <Input
                  type="time"
                  value={draft.quiet_hours_start ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, quiet_hours_start: e.target.value || null }))}
                />
              </div>
              <div>
                <Label>Quiet hours end (UTC)</Label>
                <Input
                  type="time"
                  value={draft.quiet_hours_end ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, quiet_hours_end: e.target.value || null }))}
                />
              </div>
            </div>
            <p className="-mt-2 text-[11px] text-muted-foreground">
              While inside this window we won't send you nearby-shift alerts for this watchlist. Wrap-around (e.g. 22:00 → 07:00) is supported.
            </p>
            <Button
              onClick={submit}
              disabled={busy || !draft.name.trim()}
              className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury"
            >
              {busy ? "Saving…" : "Save watchlist"}
            </Button>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-xl">Your watchlists</h2>
          {loading ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <div className="mt-4 border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No watchlists yet. Any active watchlist filters your nearby-shift notifications.
            </div>
          ) : (
            <ul className="mt-4 space-y-3">
              {items.map((w) => (
                <li key={w.id} className="border border-border bg-card p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-lg">{w.name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {(w.role_types && w.role_types.length ? w.role_types.join(", ") : "Any role")}
                        {w.min_hourly_rate != null ? ` · £${Number(w.min_hourly_rate).toFixed(2)}+/hr` : ""}
                        {w.max_distance_km != null ? ` · ≤ ${w.max_distance_km} km` : ""}
                        {w.only_dbs_eligible ? " · DBS filter on" : ""}
                        {w.quiet_hours_start && w.quiet_hours_end
                          ? ` · Quiet ${w.quiet_hours_start.slice(0, 5)}–${w.quiet_hours_end.slice(0, 5)} UTC`
                          : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => toggleActive(w)}
                        className="rounded-none tracking-wide-luxury text-xs"
                      >
                        {w.active ? "Pause" : "Resume"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => del(w.id)}
                        className="rounded-none tracking-wide-luxury text-xs text-destructive"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  {!w.active && <p className="mt-2 text-[11px] tracking-wide-luxury text-muted-foreground">Paused</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
